import { useRef, useCallback, useEffect, useState } from 'react';
import { IPCManager } from '@acrobits/ipc-sdk';

export function useIpcContactName(cloudId, contactsLowerCase) {
  const [ipcInitialized, setIpcInitialized] = useState(false);
  const [ipcManager, setIpcManager] = useState(null);
  const [phoneNameCache, setPhoneNameCache] = useState({});
  const stateRef = useRef({ ipcInitialized: false, ipcManager: null });

  // Initialize IPC
  const initializeIPC = useCallback(async () => {
    try {
    //   console.log('[IPC] Starting IPC Manager initialization...');
      const manager = new IPCManager();
    //   console.log('[IPC] IPC Manager instance created');
      const context = await manager.determineContext();
    //   console.log('[IPC] Determined context:', context);
      if (context !== 'host') {
        console.warn(`[IPC] Running in ${context} context instead of host context. Contact matching will be disabled.`);
        setIpcInitialized(true);
        stateRef.current.ipcInitialized = true;
        return;
      }
    //   console.log('[IPC] Setting up IPC Manager event listeners...');
      manager.onBadgeQueryRequest(() => {
    //     console.log('[IPC] Badge query request received');
        manager.badgeUpdate(0);
      });
      manager.onLifecycleEvent((eventName, payload) => {
    //     console.log('[IPC] Lifecycle Event:', eventName, payload);
      });
    //   console.log('[IPC] Initiating IPC connection...');
      await manager.initiateConnection();
    //   console.log('[IPC] IPC connection initiated successfully');
      setIpcManager(manager);
      setIpcInitialized(true);
      stateRef.current.ipcManager = manager;
      stateRef.current.ipcInitialized = true;
    //   console.log('[IPC] IPC Manager initialized successfully');
    } catch (err) {
      console.error('[IPC] Failed to initialize IPC Manager:', err);
      setIpcInitialized(true);
      stateRef.current.ipcInitialized = true;
    }
  }, []);

  useEffect(() => {
    if (!cloudId || !contactsLowerCase) {
    //   console.log('[IPC] Waiting for cloudId and contactsLowerCase to be available before initializing IPC.');
      return;
    }
    // console.log('[IPC] Initializing IPC with cloudId:', cloudId);
    initializeIPC();
    // eslint-disable-next-line
  }, [initializeIPC, cloudId, contactsLowerCase]);

  // Async contact matcher
  const matchContact = useCallback(
    async (phoneNumber) => {
      if (!stateRef.current.ipcInitialized || !stateRef.current.ipcManager) {
    //     console.log('[IPC] IPC not initialized or manager not available. Skipping contact match.');
        return null;
      }
      try {
    //     console.log(`[IPC] Attempting to match contact for: ${phoneNumber} (cloudId: ${cloudId || 'CREXDEMO'})`);
        const matches = await stateRef.current.ipcManager.matchContacts([
          {
            type: 'uri',
            cloudId: cloudId || 'CREXDEMO',
            uri: `tel:${phoneNumber}`,
          },
        ]);
        if (matches && matches?.length > 0) {
    //       console.log('[IPC] Contact match found:', matches[0]);
          return matches[0].displayName;
        } else {
    //       console.log('[IPC] No contact match found');
          return null;
        }
      } catch (err) {
        console.error('[IPC] Contact matching failed:', err);
        return null;
      }
    },
    [cloudId]
  );

  // Enhanced getDisplayName
  const getDisplayName = useCallback(
    (chatName) => {
      chatName = chatName.toLowerCase();
      // Group chat logic (original)
      if (chatName.startsWith('[')) {
        try {
          const members = JSON.parse(chatName);
          return members
            .map(member => {
              // Handle multiple @ symbols - keep only up to the second @
              let memberName = member;
              const atSymbols = memberName.match(/@/g);
              if (atSymbols && atSymbols?.length > 1) {
                const secondAtIndex = memberName.indexOf('@', memberName.indexOf('@') + 1);
                memberName = memberName.substring(0, secondAtIndex);
              }
              
              // Check if it's a phone number
              const isPhoneNumber = /^\+?\d+$/.test(memberName);
              if (isPhoneNumber) {
                if (phoneNameCache[memberName]) return phoneNameCache[memberName];
                // Look up in IPC contact book
                matchContact(memberName).then((name) => {
                  if (name && name !== phoneNameCache[memberName]) {
                    setPhoneNameCache((prev) => ({ ...prev, [memberName]: name }));
                  }
                });
                return phoneNameCache[memberName] || memberName;
              }

              // Try all variants of the phone number
              const myChatName = memberName.split("@")[0].toLowerCase();
              const withoutLeadingOne = myChatName.startsWith('1') && myChatName.length === 11 ? myChatName.substring(1) : null;
              const withLeadingOne = !myChatName.startsWith('1') && myChatName.length === 10 ? `1${myChatName}` : null;

              // Check all possible formats
              if (contactsLowerCase?.[memberName.toLowerCase()]) {
                return contactsLowerCase[memberName.toLowerCase()];
              }
              else if (contactsLowerCase?.[myChatName]) {
                return contactsLowerCase[myChatName];
              } else if (withoutLeadingOne && contactsLowerCase?.[withoutLeadingOne]) {
                return contactsLowerCase[withoutLeadingOne];
              } else if (withLeadingOne && contactsLowerCase?.[withLeadingOne]) {
                return contactsLowerCase[withLeadingOne];
              }
              return memberName;
            })
            .join(', ');
        } catch (error) {
          // If JSON parsing fails, return the original name
          console.error('[IPC] Error parsing group members:', error);
          return chatName;
        }
      }
      // Directory/contacts logic (original)
      // Try both with and without leading "1" for phone numbers
      const myChatName = chatName.split("@")[0];
      const withoutLeadingOne = myChatName.startsWith('1') && myChatName.length === 11 ? myChatName.substring(1).toLowerCase() : null;
      const withLeadingOne = !myChatName.startsWith('1') && myChatName.length === 10 ? `1${myChatName}`.toLowerCase() : null;
      // console.log(contactsLowerCase)
      // Check all possible formats
      if (contactsLowerCase?.[chatName]) {
        return contactsLowerCase[chatName];
      }
      else if (contactsLowerCase?.[myChatName]) {
        return contactsLowerCase[myChatName];
      } else if (withoutLeadingOne && contactsLowerCase?.[withoutLeadingOne]) {
        return contactsLowerCase[withoutLeadingOne];
      } else if (withLeadingOne && contactsLowerCase?.[withLeadingOne]) {
        return contactsLowerCase[withLeadingOne];
      }
      // If it's a phone number, try IPC contact book
      const isPhoneNumber = /^\+?\d+$/.test(myChatName);
      if (isPhoneNumber) {
        if (phoneNameCache[myChatName]) return phoneNameCache[myChatName];
        // console.log(`[IPC] Looking up phone number in IPC contact book: ${chatName}`);
        matchContact(myChatName).then((name) => {
          if (name && name !== phoneNameCache[myChatName]) {
            setPhoneNameCache((prev) => ({ ...prev, [myChatName]: name }));
          }
        });
        // console.log('[IPC] Returning chatName:', chatName);
        return phoneNameCache[myChatName] || myChatName;
      }
      // Not a group, not in contacts, not a phone number
      return myChatName;
    },
    [contactsLowerCase, matchContact, phoneNameCache]
  );

  return { getDisplayName, ipcInitialized, ipcManager };
} 